<template>
  <div class="audit-table-wrap">
    <div class="table-responsive">
      <table class="table audit-table">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key">
              <span class="audit-th">
                {{ column.label }}
                <button
                    v-for="option in column.sorts"
                    :key="option.sort"
                    type="button"
                    class="audit-sort-btn"
                    :class="{ 'is-sorted': isSorted(option.sort) }"
                    :title="'Sort by ' + column.label.toLowerCase() + ' ' + option.label.toLowerCase()"
                    @click="onClickSort(option.sort)"
                >
                  <span v-if="column.sorts.length > 1">{{ option.label }}</span>
                  <span :key="sortIcon(option.sort)" class="audit-sort-icon">
                    <i :class="sortIcon(option.sort)"></i>
                  </span>
                </button>
              </span>
            </th>
            <th class="text-end audit-actions-head">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="n in skeletonRows" :key="'skeleton-' + n">
            <td v-for="column in columns.length + 1" :key="'skeleton-cell-' + column">
              <span class="audit-skeleton"></span>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="! rows.length">
          <tr>
            <td :colspan="columns.length + 1">
              <div class="audit-empty">
                <span class="audit-empty-icon">
                  <i class="fa-solid fa-clock-rotate-left"></i>
                </span>
                <p class="audit-empty-title">No records found</p>
                <p class="audit-empty-text">Try a different search or clear some filters.</p>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="row in rows" :key="row.id">
            <td>
              <div class="audit-primary">{{ row.reference || '—' }}</div>
              <div class="audit-secondary">#{{ row.id }} · {{ row.formatted_date }}</div>
            </td>
            <td class="audit-name-cell">
              <div class="audit-primary">{{ auditRecordTitle(row) }}</div>
              <div class="audit-secondary">
                {{ auditTypeLabel(row.txn_type) }}
                <span v-if="row.description"> · {{ row.description }}</span>
              </div>
            </td>
            <td>
              <div class="audit-primary" :class="{ 'is-missing': ! row.created_by_name }">{{ auditUserLabel(row.created_by_id, row.created_by_name) }}</div>
              <div class="audit-secondary">{{ row.formatted_created_at }}</div>
            </td>
            <td>
              <div class="audit-primary" :class="{ 'is-missing': ! row.updated_by_name }">{{ auditUserLabel(row.updated_by_id, row.updated_by_name) }}</div>
              <div class="audit-secondary">
                {{ row.formatted_updated_at }}
                <span v-if="row.is_modified" class="audit-modified">Modified</span>
              </div>
            </td>
            <td class="text-end text-nowrap">
              <button
                  type="button"
                  class="btn audit-action-btn"
                  title="View History"
                  aria-label="View History"
                  @click="emit('view', row)"
              >
                <i class="fa-solid fa-clock-rotate-left"></i>
              </button>
              <button
                  type="button"
                  class="btn audit-action-btn"
                  :disabled="! canPrint(row)"
                  :title="canPrint(row) ? 'Print Voucher' : 'No voucher print available'"
                  :aria-label="canPrint(row) ? 'Print Voucher' : 'No voucher print available'"
                  @click="emit('print', row)"
              >
                <i class="fa-solid fa-print"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { auditRecordTitle, auditTypeLabel, auditUserLabel } from '@/services/utils/auditTrailFormat'

const props = defineProps({
  rows: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  sort: {
    type: String,
    default: ''
  },
  dir: {
    type: String,
    default: ''
  },
  printableTypes: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['sort', 'view', 'print'])

const skeletonRows = 6

const columns = [
  { key: 'reference', label: 'Record', sorts: [{ sort: 'reference', label: 'Ref' }] },
  { key: 'title', label: 'Name / Description', sorts: [] },
  { key: 'created', label: 'Created', sorts: [{ sort: 'created_by_name', label: 'By' }, { sort: 'created_at', label: 'Time' }] },
  { key: 'updated', label: 'Last Updated', sorts: [{ sort: 'updated_by_name', label: 'By' }, { sort: 'updated_at', label: 'Time' }] }
]

const isSorted = (sort) => {
  return props.sort === sort
}

const sortIcon = (sort) => {
  if (! isSorted(sort)) return 'fa-solid fa-sort'
  return props.dir === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'
}

const onClickSort = (sort) => {
  const dir = isSorted(sort) && props.dir !== 'asc' ? 'asc' : 'desc'
  emit('sort', { sort, dir })
}

const canPrint = (row) => {
  if (! row.voucher_type) return false
  return props.printableTypes.includes(row.voucher_type)
}
</script>

<style scoped>
.audit-table-wrap {
  border: 1px solid #eaecf0;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.audit-table {
  margin: 0;
  font-size: 13px;
}
.audit-table thead th {
  padding: 10px 12px !important;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #475467;
  white-space: nowrap;
  user-select: none;
}
.audit-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #98a2b3;
  cursor: pointer;
}
.audit-sort-btn:hover {
  background: #eaecf0;
  color: #475467;
}
.audit-sort-btn.is-sorted {
  color: #7367f0;
}
.audit-th {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.audit-sort-icon {
  font-size: 10px;
}
.audit-table tbody td {
  padding: 10px 12px !important;
  border-bottom: 1px solid #f2f4f7;
  vertical-align: middle;
  color: #344054;
}
.audit-table tbody tr:last-child td {
  border-bottom: 0;
}
.audit-table tbody tr:hover td {
  background: #fcfcfd;
}
.audit-name-cell {
  max-width: 280px;
  word-break: break-word;
}
.audit-primary {
  font-weight: 500;
  color: #101828;
}
.audit-primary.is-missing {
  font-style: italic;
  color: #b42318;
}
.audit-secondary {
  margin-top: 2px;
  font-size: 12px;
  color: #667085;
}
.audit-modified {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fffaeb;
  font-size: 11px;
  font-weight: 600;
  color: #b54708;
}
.audit-actions-head {
  width: 90px;
}
.audit-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 4px;
  padding: 0;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}
.audit-action-btn:hover:not(:disabled) {
  border-color: #7367f0;
  color: #7367f0;
}
.audit-action-btn:disabled {
  opacity: .45;
  cursor: not-allowed;
}
.audit-skeleton {
  display: block;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e4e7ec 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: audit-shimmer 1.2s infinite;
}
.audit-empty {
  padding: 40px 16px;
  text-align: center;
}
.audit-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  border-radius: 50%;
  background: #f4f3ff;
  font-size: 20px;
  color: #7367f0;
}
.audit-empty-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}
.audit-empty-text {
  margin: 4px 0 0;
  font-size: 13px;
  color: #667085;
}
@keyframes audit-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
